import { useState, useEffect } from "react";
import { Storage } from "aws-amplify";
import { useUpdateAppealMutation } from "../../../endpoints/updateAppeal";
import { useLazyGetArtifactDetailsByIdQuery } from "../../../endpoints/getArtifactDetailsById";
import { Attachment, UseSubmitAttachmentsProps } from "./types";
import { generatePdf } from "../../appeal-pdf/PDFGenerator";

export const useAttachmentSelection = (attachments: Attachment[]) => {
    const [selected, setSelected] = useState<{ [key: string]: boolean }>({});

    useEffect(() => {
        if (attachments) {
            const initialSelected = attachments.reduce(
                (acc, attachment) => {
                    acc[attachment.canopyS3Key] = attachment.enable || false;

                    return acc;
                },
                {} as { [key: string]: boolean }
            );
            setSelected(initialSelected);
        }
    }, [attachments]);

    const onChangeRow = (id: string) => (value: boolean) => {
        setSelected(prev => ({ ...prev, [id]: value }));
    };

    const handleSelectAll = () => {
        const allSelected = attachments.reduce(
            (acc, attachment) => {
                acc[attachment.canopyS3Key] = true;

                return acc;
            },
            {} as { [key: string]: boolean }
        );
        setSelected(allSelected);
    };

    const handleClearSelection = () => {
        const allCleared = attachments.reduce(
            (acc, attachment) => {
                acc[attachment.canopyS3Key] = false;

                return acc;
            },
            {} as { [key: string]: boolean }
        );
        setSelected(allCleared);
    };

    return { selected, onChangeRow, handleSelectAll, handleClearSelection };
};

export const useValueNotices = () => {
    const [getArtifactDetailsById] = useLazyGetArtifactDetailsByIdQuery();

    const getValueNoticesDownloadUrl = async (valueNotices: any[]) => {
        const valueNoticeUrlList: string[] = [];
        const valueNoticeArtifactIds = valueNotices
            .filter(valueNotice => valueNotice.enable === true)
            .map(valueNotice => valueNotice.artifactId);

        await Promise.all(
            valueNoticeArtifactIds.map(async artifactId => {
                const response = await getArtifactDetailsById({
                    artifactType: "DOCUMENT",
                    artifactId: artifactId,
                    businessUseCase: "SMART",
                });
                if (response.data?.artifactDetails?.documentS3PreSignedUrl) {
                    valueNoticeUrlList.push(response.data.artifactDetails.documentS3PreSignedUrl);
                }
            })
        );

        return valueNoticeUrlList;
    };

    return { getValueNoticesDownloadUrl };
};

export const usePresignedUrl = () => {
    const fetchPresignedUrl = async (attachment: Attachment) => {
        try {
            const presignedUrl = await Storage.get(attachment.canopyS3Key, {
                expires: 300,
                download: false,
                contentDisposition: "inline",
                contentType: "application/pdf",
            });
            window.open(presignedUrl, "_blank");
        } catch (error) {
            console.error(`Error loading image ${attachment.canopyS3Key}:`, error);
        }
    };

    return { fetchPresignedUrl };
};

export const useSubmitAttachments = ({
    data,
    attachments,
    selected,
    alias,
    onNext,
}: UseSubmitAttachmentsProps) => {
    const [pdfLoading, setPdfLoading] = useState(false);
    const [updateAppeal] = useUpdateAppealMutation();
    const { getValueNoticesDownloadUrl } = useValueNotices();

    const handleSubmitAttachments = async () => {
        try {
            const updatedData = JSON.parse(JSON.stringify(data));

            if (
                updatedData.appealDetails?.appealApproachCalculationData.exportAppealsPackage
                    .attachments
            ) {
                updatedData.appealDetails.appealApproachCalculationData.exportAppealsPackage.attachments =
                    attachments.map(attachment => ({
                        ...attachment,
                        enable: selected[attachment.canopyS3Key] || false,
                    }));
            }

            await updateAppeal({
                businessUseCase: "SMART",
                appealArtifactId: data.appealArtifactId,
                appealDetails: updatedData.appealDetails,
                appealStatus: data.appealDetails.status,
                clientName: alias,
            });

            setPdfLoading(true);
            const valueNoticeUrls = await getValueNoticesDownloadUrl(
                data.appealDetails.appealApproachCalculationData.exportAppealsPackage.valueNotices
            );
            const pdfBlob = await generatePdf(updatedData.appealDetails, valueNoticeUrls);
            setPdfLoading(false);
            onNext(pdfBlob);
        } catch (error) {
            console.error("Error submitting attachments:", error);
            setPdfLoading(false);
        }
    };

    return { handleSubmitAttachments, pdfLoading };
};
